import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { todayISO } from '../../lib/format';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { Notice } from '../ui/Badge';
import { CustomerPicker } from '../ui/CustomerPicker';
import { Field, field } from './Field';

/**
 * Setting up repeating work. The schedule is the whole difficulty, and the answer is to
 * ask for it the way a shop already says it out loud — "twice a year, spring and fall" —
 * rather than exposing the rrule-shaped object underneath.
 *
 * Four presets cover what a home-service shop actually sells. Anything rarer is a job
 * somebody books by hand, and a builder UI for the long tail would tax every shop that
 * only ever needed "every 6 months".
 */

type PresetKey = 'twice_a_year' | 'quarterly' | 'monthly' | 'yearly';

const PRESETS: {
  key: PresetKey;
  label: string;
  hint: string;
  schedule: { freq: 'monthly' | 'yearly'; interval: number; months?: number[] };
}[] = [
  {
    key: 'twice_a_year',
    label: 'Twice a year',
    hint: 'Spring and fall — the classic maintenance plan.',
    schedule: { freq: 'monthly', interval: 6 },
  },
  {
    key: 'quarterly',
    label: 'Every three months',
    hint: 'Four visits a year.',
    schedule: { freq: 'monthly', interval: 3 },
  },
  {
    key: 'monthly',
    label: 'Every month',
    hint: 'Twelve visits a year.',
    schedule: { freq: 'monthly', interval: 1 },
  },
  {
    key: 'yearly',
    label: 'Once a year',
    hint: 'An annual service or inspection.',
    schedule: { freq: 'yearly', interval: 1 },
  },
];

const BILLING: { key: 'per_visit' | 'monthly' | 'annual'; label: string }[] = [
  { key: 'per_visit', label: 'Each visit' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'annual', label: 'Once a year' },
];

// (was `const today = () => new Date().toISOString().slice(0, 10)` — the UTC date, which
// started a contract a day early for any shop west of Greenwich after 8 PM.)

export function NewAgreementSheet({
  open,
  onClose,
  tz,
}: {
  open: boolean;
  onClose: () => void;
  /** The shop's zone — what date the contract pre-fills its start as. */
  tz: string;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [who, setWho] = useState({ customerId: '', propertyId: '' });
  const [name, setName] = useState('');
  const [preset, setPreset] = useState<PresetKey>('twice_a_year');
  const [startDate, setStartDate] = useState(todayISO(tz));
  const [billingMode, setBillingMode] = useState<'per_visit' | 'monthly' | 'annual'>(
    'per_visit',
  );
  const [price, setPrice] = useState('');

  const create = useMutation(
    trpc.agreement.create.mutationOptions({
      onSuccess: (r) => {
        void qc.invalidateQueries();
        setWho({ customerId: '', propertyId: '' });
        setName('');
        setPrice('');
        onClose();
        void navigate({
          to: '/agreements/$agreementId',
          params: { agreementId: r.id },
        });
      },
    }),
  );

  const picked = PRESETS.find((p) => p.key === preset)!;
  const dollars = Number(price.replace(/[$,\s]/g, ''));
  const priceOk = price.trim() === '' || (Number.isFinite(dollars) && dollars >= 0);
  const valid = who.customerId && who.propertyId && name.trim().length > 0 && priceOk;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="New repeating job"
      footer={
        <Button
          className="w-full"
          disabled={!valid || create.isPending}
          onClick={() =>
            create.mutate({
              customerId: who.customerId,
              propertyId: who.propertyId,
              name: name.trim(),
              schedule: picked.schedule,
              billingMode,
              priceCents: price.trim() === '' ? 0 : Math.round(dollars * 100),
              startDate,
              ...idem(),
            })
          }
        >
          {create.isPending ? 'Setting it up…' : 'Set it up'}
        </Button>
      }
    >
      <div className="space-y-3">
        {create.error ? <Notice tone="danger">{create.error.message}</Notice> : null}

        <CustomerPicker
          customerId={who.customerId}
          propertyId={who.propertyId}
          onChange={setWho}
        />

        <Field label="What is the work?">
          <input
            className={field}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Heating + cooling maintenance plan"
          />
        </Field>

        <fieldset className="space-y-1.5">
          <legend className="mb-1 block text-xs font-medium text-muted-foreground">
            How often
          </legend>
          {PRESETS.map((p) => (
            <label
              key={p.key}
              className={`flex cursor-pointer gap-2.5 rounded-[var(--radius)] border p-2.5 ${
                p.key === preset ? 'border-brand bg-brand/5' : 'border-border'
              }`}
            >
              <input
                type="radio"
                name="repeat"
                className="mt-1"
                checked={p.key === preset}
                onChange={() => setPreset(p.key)}
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium">{p.label}</span>
                <span className="block text-xs text-muted-foreground">{p.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <div className="grid grid-cols-2 gap-2">
          <Field label="First visit on">
            <input
              className={field}
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Field>
          <Field label="Billed">
            <select
              className={field}
              value={billingMode}
              onChange={(e) =>
                setBillingMode(e.target.value as 'per_visit' | 'monthly' | 'annual')
              }
            >
              {BILLING.map((b) => (
                <option key={b.key} value={b.key}>
                  {b.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Price (leave blank if it's billed some other way)">
          <input
            className={field}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="189.00"
            inputMode="decimal"
          />
        </Field>
        {!priceOk ? <Notice tone="warn">That doesn't look like an amount.</Notice> : null}
      </div>
    </Sheet>
  );
}
